import { EndpointBuilder } from '@reduxjs/toolkit/query/react';
import { MailsResponse, MailSettings, SingleForwardingStatus } from './types';
import { ApiBaseQuery, ApiTagTypes } from '../types';

/**
 * Endpoints для почты
 */
export const mailsEndpoints = (builder: EndpointBuilder<ApiBaseQuery, ApiTagTypes, 'api'>) => ({
    /**
     * Получение настроек почты
     */
    getMailSettings: builder.query<Pick<MailsResponse, 'settings' | 'single_forwarding'>, void>({
        query: () => ({
            url: '/private/mails',
        }),
        transformResponse: (response: MailsResponse) => {
            return {
                settings: response.settings,
                single_forwarding: response.single_forwarding,
            };
        },
        providesTags: ['MailSettings'],
    }),
    /**
     * Получение списка писем
     */
    getMails: builder.infiniteQuery<MailsResponse, void, string | undefined>({
        infiniteQueryOptions: {
            initialPageParam: undefined,
            getNextPageParam: (lastPage) => {
                // Возвращаем cursor для следующей страницы, если он есть
                return lastPage.cursor;
            },
        },
        query: ({ pageParam }) => {
            const queryParams = new URLSearchParams();
            // queryParams.append('test', 'true');
            if (pageParam) {
                queryParams.append('cursor', pageParam);
            }
            const queryString = queryParams.toString();
            return {
                url: `/private/mails${queryString ? `?${queryString}` : ''}`,
            };
        },
        providesTags: ['Mails'],
    }),
    /**
     * Сохранение настроек почты
     */
    saveMailSettings: builder.mutation<MailSettings, MailSettings>({
        query: (body) => ({
            url: '/private/mails/settings',
            method: 'POST',
            body,
        }),

        // Обновляем кеш напрямую данными из ответа, без нового запроса
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
            try {
                const { data: updatedSettings } = await queryFulfilled;
                // Обновляем настройки в кеше getMails
                const { api } = await import('../index');
                dispatch(
                    api.util.updateQueryData('getMails', undefined, (draft) => {
                        if (draft && draft.pages && draft.pages.length > 0 && draft.pages[0]) {
                            // Обновляем настройки в первой странице
                            draft.pages[0].settings = updatedSettings;
                        }
                    }),
                );
            } catch {
                // В случае ошибки ничего не делаем
            }
        },
    }),
    /**
     * Заказ единоразовой пересылки
     */
    orderOneTimeForwarding: builder.mutation<void, { status: SingleForwardingStatus }>({
        query: ({ status }) => {
            const queryParams = new URLSearchParams();
            queryParams.append('status', status);
            return {
                url: `/private/mails/forwarding?${queryParams.toString()}`,
                method: 'POST',
            };
        },
        // Инвалидируем кеш getMails и getMailSettings, чтобы обновить информацию о single_forwarding
        invalidatesTags: ['MailSettings'],
    }),
});
